import { Component, OnInit, Input, TemplateRef } from '@angular/core';
import { Q2AngularcmsService } from '../../q2-angular-cms.service';
import { MatDialog } from '@angular/material/dialog';
import { CmsEditFormComponent } from '../cms-edit-form/cms-edit-form.component';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface CMS {
  id: Number;
  /**
   * Unique identifier that tells which data to pull from the service
   */
  panelKey: string;
  panelTitle: string;
  panelTitleClass: any;
  panelTitleIcon: any;
  panelClass: any;
  hasHeader: any;
  panelValue: string;
  panelEditors: string;
  version: Number;
  isVisible: Number;
  comments: string;
  createdDate: string;
  updatedDate: string;
  creatorUid: string;
  updatedUid: string;
  updatedDateFmt: string;
  updatedName: string;
}

interface IQ2Environment {
  hostname: string;
  env: string;
  serviceUrl: any;
}

interface IQ2Environments extends Array<IQ2Environment> { }

@Component({
  selector: 'app-cms-widget',
  templateUrl: 'cms.component.html',
  styleUrls: ['cms.component.scss']
})
export class CmsComponent implements OnInit {

  _panelKey: string;

  /**
   * Service to look at for CMS Widgets.
   */
  modelName: string = 'Q2AdminPortal/Core/CMS/cmsPanel';
  public cmsWidget: CMS;
  public editCMS: any;
  isLoading = false;
  config: any;
  env: any;
  url: any;

  @Input() set panelKey(value: string) {
    this._panelKey = value;
    if (this.env && this.url) {
      this.getCMSWidget();
    }
  };
  @Input() siteSettings: any;
  @Input() appSettings: any;
  @Input() classes: string[];
  @Input() noDataBodyTemplate: TemplateRef<any>;
  @Input() noDataHeaderTemplate: TemplateRef<any>;
  @Input() loadingTemplate: TemplateRef<any>;

  constructor(private q2CMSService: Q2AngularcmsService, private dialog: MatDialog, private snackbar: MatSnackBar) { }

  ngOnInit() {
    this.config = JSON.parse(localStorage.getItem('Q2Config'));
    this.env = this.getEnv();
    this.url = this.getAPIRoot();
    this.getCMSWidget();
  }

  public getCMSWidget() {
    this.isLoading = true;
    this.q2CMSService.read(this.env, this.url, this.modelName, { panelKey: this._panelKey }).subscribe((data: CMS) => {
      this.isLoading = false;
      this.cmsWidget = data[0];
    });
  }

  public getEnv() {
    if (this.config) {
      let env: string;
      // tslint:disable-next-line:prefer-for-of
      for (let i = 0; i < this.config.length; i++) {
        const element: IQ2Environment = this.config[i];
        if (element.hostname === window.location.hostname) {
          env = element.env;
        }
      }
      return env;
    } else {
      this.config = JSON.parse(localStorage.getItem('Q2Config'));
      this.getEnv();
    }
  }

  /**
   * getAPIRoot()
   * @returns Q2DM API Root path
   */
  public getAPIRoot() {
    if (this.config) {
      let serviceUrl: string;
      // tslint:disable-next-line:prefer-for-of
      for (let i = 0; i < this.config.length; i++) {
        const element: IQ2Environment = this.config[i];
        if (element.hostname === window.location.hostname) {
          serviceUrl = element.serviceUrl;
        }
      }
      return serviceUrl;
    } else {
      this.config = JSON.parse(localStorage.getItem('Q2Config'));
      this.getAPIRoot();
    }
  }

  /**
   * editWidget() looks to see if the provided panelKey matches any in the service.
   * If it does, it returns that data. Else, it returns a blank CMS widget to be edited and saved with that panelKey.
   */
  editWidget() {
    if (this.cmsWidget && (this.cmsWidget.hasHeader == true || this.cmsWidget.hasHeader === 'true')) {
      this.cmsWidget.hasHeader = true;
    } else if (this.cmsWidget && (this.cmsWidget.hasHeader == false || this.cmsWidget.hasHeader === 'false')) {
      this.cmsWidget.hasHeader = false;
    }
    const dialogRef = this.dialog.open(CmsEditFormComponent, {
      width: '50%',
      data: {
        cms: this.cmsWidget ? this.cmsWidget : { hasHeader: true, panelEditors: 'Everyone', panelKey: this.panelKey },
        classes: this.classes
      },
      disableClose: true
    });
    dialogRef.afterClosed().subscribe(data => {
      if (data) {
        const create = this.cmsWidget ? false : true;
        this.cmsWidget = data;
        this.saveHandler(data, create);
      } else {
        this.getCMSWidget();
      }
    });
  }

  /**
  *saveHandler() bundles everything up into cmsWidget class and updates an existing CMSWidget (if panelKey matched one) or creates a new CMSWidget.
  */
  saveHandler(event: any, create: boolean) {
    if (event.hasHeader) {
      event.hasHeader = 'true';
    } else {
      event.hasHeader = 'false';
    }
    if (this.cmsWidget) {
      this.cmsWidget.panelTitleClass ? this.cmsWidget.panelTitleClass = event.panelTitleClass : this.cmsWidget.panelTitleClass = null;
      this.cmsWidget.panelClass ? this.cmsWidget.panelClass = event.panelClass : this.cmsWidget.panelClass = null;
    }
    if (!create) {
      this.q2CMSService.update(this.env, this.url, this.modelName, event).subscribe(val => {
        this.editCMS = undefined;
        this.getCMSWidget();
        this.snackbar.open('CMS Widget Updated!', '', {
          duration: 2000
        });
      });
    } else {
      event.panelTitleClass = event.panelTitleClass;
      event.panelClass = event.panelClass ? event.panelClass : null;
      this.q2CMSService.create(this.env, this.url, this.modelName, event).subscribe(val => {
        this.editCMS = undefined;
        this.getCMSWidget();
        this.snackbar.open('CMS Widget Updated!', '', {
          duration: 2000
        });
      });
    }

  }

  /**
  *getToolTip() checks to see if the CMS data has any comments. If it does, that is the tooltip for the widget.
  * If not, the last modified date is used as the tooltip.
  * The tooltip is displayed when hovering over the info icon of the widget.
  * @returns comments for that CMS Widget (cmsWidget.comments)
  */
  getToolTip() {
    if (this.cmsWidget.comments === null || this.cmsWidget.comments === undefined || this.cmsWidget.comments.trim() === '') {
      return this.cmsWidget.updatedDate;
    } else {
      return this.cmsWidget.comments;
    }
  }

  /**
   * canEdit() looks at the panelEditors attribute of the CMSWidget and splits it on commas.
   * This creates an array of users who are authorized to edit the widget.
   * If the current user's permissions are not in said array, they cannot edit it and the edit button disappears.
   * @returns boolean
   */
  canEdit() {
    const editors = this.cmsWidget.panelEditors.split(',');
    if (editors.some((val) => this.appSettings.memberOfArray.indexOf(val) !== -1)) {
      return true;
    } else {
      return false;
    }
  }

}
